import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BadgePercent, FileText, Building2, CreditCard, User, Info, Upload, MapPin, Phone, Mail, X, CheckCircle, AlertTriangle, FileImage } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { API, useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const INDIAN_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Chandigarh', 'Jammu & Kashmir', 'Ladakh', 'Puducherry'
];

const REQUIRED_DOCUMENTS = [
    { key: 'aadhaar_doc', label: 'Aadhaar Card (Front & Back)', hint: 'Clear photo/scan of your Aadhaar card', required: true },
    { key: 'pan_doc', label: 'PAN Card', hint: 'Clear photo/scan of PAN card', required: true },
    { key: 'electricity_bill_doc', label: 'Latest Electricity Bill', hint: 'Most recent bill showing consumer number', required: true },
    { key: 'bank_passbook_doc', label: 'Bank Passbook / Cancelled Cheque', hint: 'First page showing name, account no, IFSC', required: true },
    { key: 'property_proof_doc', label: 'Property Ownership Proof / NOC', hint: 'Registry, tax receipt, or NOC from owner if rented', required: false },
    { key: 'photo_doc', label: 'Passport Size Photo', hint: 'Recent passport size photograph', required: false }
];

const SubsidyForm = ({ formData, setFormData, claimSubsidy, setClaimSubsidy }) => {
    const { token } = useAuth();
    const [uploadingDoc, setUploadingDoc] = useState(null);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            subsidy_details: {
                ...prev.subsidy_details,
                [field]: value
            }
        }));
    };

    const handleDocUpload = async (docKey, file) => {
        if (!file) return;

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File too large. Maximum 5MB allowed.');
            return;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Invalid file type. Only JPG, PNG, and PDF are accepted.');
            return;
        }

        setUploadingDoc(docKey);
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);

            const res = await axios.post(`${API}/api/upload`, formDataUpload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            const uploadedUrl = res.data.url || res.data.file_url;
            if (!uploadedUrl) {
                toast.error('Upload succeeded but no URL returned');
                return;
            }
            handleChange(docKey, uploadedUrl);
            toast.success('Document uploaded successfully');
        } catch (error) {
            toast.error('Failed to upload document');
        } finally {
            setUploadingDoc(null);
        }
    };

    const removeDoc = (docKey) => {
        handleChange(docKey, null);
    };

    const sd = formData.subsidy_details || {};

    return (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 rounded-xl border border-green-200 dark:border-green-800 p-4 md:p-6">
            <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4 mb-4 md:mb-6">
                <div className="flex items-start gap-3 flex-1">
                    <div className="flex h-8 w-8 md:h-10 md:w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900 border border-green-200 dark:border-green-700 flex-shrink-0">
                        <BadgePercent className="h-4 w-4 md:h-5 md:w-5 text-green-700 dark:text-green-300" />
                    </div>
                    <div className="flex-1 space-y-1 min-w-0">
                        <h3 className="font-semibold text-green-900 dark:text-green-100 flex flex-wrap items-center gap-2 text-sm md:text-base">
                            PM Surya Ghar Subsidy
                            <span className="text-[10px] md:text-xs px-2 py-0.5 rounded-full bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 font-medium">
                                Up to ₹78,000 Off
                            </span>
                        </h3>
                        <p className="text-xs md:text-sm text-green-700 dark:text-green-300">
                            Claim government subsidy by providing required details and documents.
                        </p>
                    </div>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                    <Checkbox
                        id="claim_subsidy"
                        checked={claimSubsidy}
                        onCheckedChange={setClaimSubsidy}
                    />
                    <Label htmlFor="claim_subsidy" className="cursor-pointer font-medium text-sm">Claim Subsidy</Label>
                </div>
            </div>

            <AnimatePresence>
                {claimSubsidy && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="space-y-4 md:space-y-6 pt-2 border-t border-green-200 dark:border-green-800">
                            <Alert className="bg-green-100/50 border-green-200 text-green-800">
                                <Info className="h-4 w-4" />
                                <AlertTitle>Important</AlertTitle>
                                <AlertDescription>
                                    All details must match your Aadhaar card and Electricity bill exactly. Wrong or mismatched information will lead to subsidy rejection. Upload clear scans/photos of all documents.
                                </AlertDescription>
                            </Alert>

                            {/* ====== SECTION 1: Personal Details ====== */}
                            <div className="space-y-4">
                                <h4 className="font-medium flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
                                    <User className="h-4 w-4" /> Personal Details (As per Aadhaar)
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                    <div className="space-y-2">
                                        <Label>Full Name (as per Aadhaar) <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.aadhaar_name || ''}
                                            onChange={e => handleChange('aadhaar_name', e.target.value)}
                                            placeholder="Enter full name as on Aadhaar"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Aadhaar Number <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.aadhaar_number || ''}
                                            onChange={e => handleChange('aadhaar_number', e.target.value.replace(/\D/g, ''))}
                                            placeholder="12-digit Aadhaar number"
                                            maxLength={12}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>PAN Number <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.pan_number || ''}
                                            onChange={e => handleChange('pan_number', e.target.value.toUpperCase())}
                                            placeholder="e.g. ABCDE1234F"
                                            maxLength={10}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Mobile Number <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.mobile_number || ''}
                                            onChange={e => handleChange('mobile_number', e.target.value.replace(/\D/g, ''))}
                                            placeholder="10-digit mobile number"
                                            maxLength={10}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Email Address <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.email || ''}
                                            onChange={e => handleChange('email', e.target.value)}
                                            placeholder="your@email.com"
                                            type="email"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Date of Birth</Label>
                                        <Input
                                            value={sd.date_of_birth || ''}
                                            onChange={e => handleChange('date_of_birth', e.target.value)}
                                            type="date"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ====== SECTION 2: Installation Address ====== */}
                            <div className="space-y-4">
                                <h4 className="font-medium flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
                                    <MapPin className="h-4 w-4" /> Installation Address
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                    <div className="md:col-span-2 space-y-2">
                                        <Label>Full Address <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.installation_address || ''}
                                            onChange={e => handleChange('installation_address', e.target.value)}
                                            placeholder="House/Flat No, Street, Area, Landmark"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>State <span className="text-red-500">*</span></Label>
                                        <Select
                                            value={sd.state}
                                            onValueChange={v => handleChange('state', v)}
                                        >
                                            <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                                            <SelectContent>
                                                {INDIAN_STATES.map(s => (
                                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>District <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.district || ''}
                                            onChange={e => handleChange('district', e.target.value)}
                                            placeholder="e.g. Jaipur"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>PIN Code <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.pincode || ''}
                                            onChange={e => handleChange('pincode', e.target.value.replace(/\D/g, ''))}
                                            placeholder="6-digit PIN"
                                            maxLength={6}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ====== SECTION 3: Electricity Connection ====== */}
                            <div className="space-y-4">
                                <h4 className="font-medium flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
                                    <FileText className="h-4 w-4" /> Electricity Connection Details
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                    <div className="space-y-2">
                                        <Label>Consumer Number <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.electricity_consumer_number || ''}
                                            onChange={e => handleChange('electricity_consumer_number', e.target.value)}
                                            placeholder="From your electricity bill"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Distribution Company (DISCOM) <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.discom_name || ''}
                                            onChange={e => handleChange('discom_name', e.target.value)}
                                            placeholder="e.g. JVVNL, BESCOM, TATA Power"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Connection Type</Label>
                                        <Select
                                            value={sd.connection_type}
                                            onValueChange={v => handleChange('connection_type', v)}
                                        >
                                            <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="single_phase">Single Phase</SelectItem>
                                                <SelectItem value="three_phase">Three Phase</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Sanctioned Load (kW)</Label>
                                        <Input
                                            value={sd.sanctioned_load || ''}
                                            onChange={e => handleChange('sanctioned_load', e.target.value)}
                                            placeholder="e.g. 3"
                                            type="number"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Property Ownership <span className="text-red-500">*</span></Label>
                                        <Select
                                            value={sd.property_ownership}
                                            onValueChange={v => handleChange('property_ownership', v)}
                                        >
                                            <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="owned">Self-Owned</SelectItem>
                                                <SelectItem value="rented_with_noc">Rented (with NOC from owner)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Average Monthly Electricity Bill (₹)</Label>
                                        <Input
                                            value={sd.avg_monthly_bill || ''}
                                            onChange={e => handleChange('avg_monthly_bill', e.target.value)}
                                            placeholder="e.g. 3000"
                                            type="number"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ====== SECTION 4: Bank Details ====== */}
                            <div className="space-y-4">
                                <h4 className="font-medium flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
                                    <CreditCard className="h-4 w-4" /> Bank Account for Subsidy Disbursement
                                </h4>
                                <Alert className="bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/20 dark:border-amber-800 dark:text-amber-300">
                                    <AlertTriangle className="h-4 w-4" />
                                    <AlertDescription className="text-xs">
                                        Bank account must be linked to your Aadhaar. Subsidy amount will be directly credited to this account by the government.
                                    </AlertDescription>
                                </Alert>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                    <div className="space-y-2">
                                        <Label>Account Holder Name <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.bank_account_name || ''}
                                            onChange={e => handleChange('bank_account_name', e.target.value)}
                                            placeholder="Name as on bank account"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Account Number <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.bank_account_number || ''}
                                            onChange={e => handleChange('bank_account_number', e.target.value)}
                                            placeholder="Bank account number"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Confirm Account Number <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.bank_account_number_confirm || ''}
                                            onChange={e => handleChange('bank_account_number_confirm', e.target.value)}
                                            placeholder="Re-enter account number"
                                        />
                                        {sd.bank_account_number && sd.bank_account_number_confirm && sd.bank_account_number !== sd.bank_account_number_confirm && (
                                            <p className="text-xs text-red-500">Account numbers don't match</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label>IFSC Code <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.bank_ifsc_code || ''}
                                            onChange={e => handleChange('bank_ifsc_code', e.target.value.toUpperCase())}
                                            placeholder="e.g. SBIN0001234"
                                            maxLength={11}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Bank Name <span className="text-red-500">*</span></Label>
                                        <Input
                                            value={sd.bank_name || ''}
                                            onChange={e => handleChange('bank_name', e.target.value)}
                                            placeholder="e.g. State Bank of India"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Branch Name</Label>
                                        <Input
                                            value={sd.bank_branch || ''}
                                            onChange={e => handleChange('bank_branch', e.target.value)}
                                            placeholder="e.g. Main Branch, Jaipur"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ====== SECTION 5: Document Uploads ====== */}
                            <div className="space-y-4">
                                <h4 className="font-medium flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
                                    <Upload className="h-4 w-4" /> Required Documents
                                </h4>
                                <p className="text-xs text-muted-foreground">
                                    Upload clear scans or photos. Accepted formats: JPG, PNG, PDF (max 5MB each)
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                    {REQUIRED_DOCUMENTS.map(doc => (
                                        <div key={doc.key} className="bg-white dark:bg-card rounded-lg border p-3 md:p-4 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label className="text-sm font-medium">
                                                    {doc.label}
                                                    {doc.required && <span className="text-red-500 ml-1">*</span>}
                                                </Label>
                                                {sd[doc.key] && (
                                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground">{doc.hint}</p>

                                            {sd[doc.key] ? (
                                                <div className="flex items-center gap-2 bg-green-50 dark:bg-green-950/20 p-2 rounded border border-green-200 dark:border-green-800">
                                                    <FileImage className="w-4 h-4 text-green-600" />
                                                    <span className="text-xs text-green-700 dark:text-green-300 flex-1 truncate">Uploaded ✓</span>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                                        onClick={() => removeDoc(doc.key)}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <label className="flex items-center justify-center gap-2 cursor-pointer bg-muted/50 hover:bg-muted border-2 border-dashed rounded-lg p-3 transition-colors">
                                                    {uploadingDoc === doc.key ? (
                                                        <span className="text-xs text-muted-foreground animate-pulse">Uploading...</span>
                                                    ) : (
                                                        <>
                                                            <Upload className="w-4 h-4 text-muted-foreground" />
                                                            <span className="text-xs text-muted-foreground">Click to upload</span>
                                                        </>
                                                    )}
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        accept=".jpg,.jpeg,.png,.pdf"
                                                        onChange={(e) => {
                                                            handleDocUpload(doc.key, e.target.files[0]);
                                                            e.target.value = '';
                                                        }}
                                                        disabled={uploadingDoc === doc.key}
                                                    />
                                                </label>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Declaration */}
                            <Alert className="bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950/20 dark:border-blue-800 dark:text-blue-300">
                                <Info className="h-4 w-4" />
                                <AlertTitle className="text-sm">Declaration</AlertTitle>
                                <AlertDescription className="text-xs">
                                    By submitting this application, I confirm that all provided information is true and accurate.
                                    I authorize SolarSaver to submit this subsidy application to the Ministry of New & Renewable Energy (MNRE)
                                    under PM Surya Ghar Muft Bijli Yojana on my behalf. I understand that providing false information may
                                    result in subsidy rejection and/or legal action.
                                </AlertDescription>
                            </Alert>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SubsidyForm;
